<template>
  <PageContainer title="Emissão de LADV">
    <Box space="medium" class="ladv-container">
      <ol class="etapas">
        <li v-for="(titulo, index) in etapas" :key="titulo"
          :class="{ ativa: etapa === index + 1, concluida: etapa > index + 1 }">
          <span class="numero">
            <Check v-if="etapa > index + 1" :size="16" />
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span class="titulo">{{ titulo }}</span>
        </li>
      </ol>

      <div v-if="carregandoAluno" class="d-flex justify-content-center">
        <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="60" />
      </div>

      <!-- Etapa 1: dados do candidato -->
      <form v-else-if="etapa === 1" class="etapa" @submit.prevent="emitir">
        <p class="descricao">
          Informe o candidato. O Detran valida o processo, gera a LADV e envia um código de confirmação para o
          e-mail do candidato.
        </p>

        <div v-if="aluno" class="aviso">
          <InformationOutline :size="20" />
          <span>
            Para uma <strong>nova categoria</strong>, confirme que o novo processo já foi aberto no Detran e,
            se o RENACH mudou, atualize-o abaixo.
          </span>
        </div>

        <div class="inputs">
          <Input v-if="aluno" class="input-full" legend="Aluno" :modelValue="aluno.nome" readonly />

          <Input class="input-full" mask="###.###.###-##" legend="CPF do candidato" placeholder="000.000.000-00"
            :modelValue="cpf" @update:modelValue="$event => (cpf = $event)" :disabled="loading || !!aluno" />

          <Input class="input-full" legend="RENACH" placeholder="CE123456789" :modelValue="renach"
            @update:modelValue="$event => (renach = $event.toUpperCase().replace(/\s/g, ''))" :disabled="loading" />

          <Input class="input-full" legend="Instrutor responsável" :modelValue="instrutor" readonly />
        </div>

        <div class="buttons">
          <Button type="button" text="Voltar" color="danger" :fill="false" :disabled="loading"
            @handle:click="voltar" />
          <Button type="submit" :text="loading ? 'Emitindo...' : 'Emitir LADV'" color="primary" :disabled="loading">
            <CardAccountDetailsOutline :size="18" />
          </Button>
        </div>
      </form>

      <!-- Etapa 2: código enviado por e-mail -->
      <form v-else-if="etapa === 2" class="etapa" @submit.prevent="liberarImagem">
        <div class="email-enviado">
          <EmailCheckOutline :size="40" />
          <div>
            <h4>Código enviado</h4>
            <p>
              Enviamos um código de 6 dígitos para
              <strong>{{ ladv.email_mascarado || 'o e-mail do candidato' }}</strong>.
              Ele vale por 24 horas: se preferir, saia e continue depois pela lista de emissões abaixo.
            </p>
          </div>
        </div>

        <div class="inputs">
          <Input class="input-codigo" mask="######" legend="Código de confirmação" placeholder="000000"
            :modelValue="codigo" @update:modelValue="$event => (codigo = $event)" :disabled="loading" />
        </div>

        <div class="buttons">
          <Button type="button" color="secondary" :fill="false" :disabled="loading || cooldown > 0"
            :text="cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'"
            @handle:click="reenviarCodigo">
            <Refresh :size="18" />
          </Button>
          <Button type="submit" :text="loading ? 'Validando...' : 'Liberar LADV'" color="primary"
            :disabled="loading">
            <Check :size="18" />
          </Button>
        </div>
      </form>

      <!-- Etapa 3: documento -->
      <div v-else class="etapa">
        <div class="documento">
          <img :src="imagemSrc" alt="LADV emitida" />
        </div>

        <div class="buttons">
          <Button type="button" text="Nova emissão" color="secondary" :fill="false" @handle:click="reiniciar" />
          <Button type="button" text="Imprimir" color="primary" :fill="false" @handle:click="imprimir">
            <Printer :size="18" />
          </Button>
          <Button type="button" text="Baixar" color="primary" @handle:click="baixar">
            <Download :size="18" />
          </Button>
        </div>
      </div>

      <!-- Resultado do cadastro local do aluno (feito logo após a emissão) -->
      <div v-if="etapa > 1 && cadastroAluno" class="cadastro-aluno" :class="cadastroAluno.sucesso ? 'ok' : 'falha'">
        <CheckCircleOutline v-if="cadastroAluno.sucesso" :size="22" />
        <AlertCircleOutline v-else :size="22" />

        <div class="texto">
          <strong>Cadastro do aluno</strong>
          <span>{{ cadastroAluno.mensagem }}</span>
        </div>

        <Button v-if="!cadastroAluno.sucesso" type="button" text="Tentar novamente" color="primary" :fill="false"
          size="small" :disabled="vinculando" @handle:click="vincularAluno" />
        <Button v-else-if="!aluno" type="button" text="Ver alunos" color="primary" :fill="false" size="small"
          @handle:click="$router.push({ name: 'listagem-alunos' })" />
      </div>
    </Box>

    <Box space="medium" class="emissoes-container">
      <div class="emissoes-header">
        <h3>{{ aluno ? 'Emissões do aluno' : 'Emissões aguardando código' }}</h3>
        <IconButton color="secondary" title="Atualizar lista" :disabled="carregandoEmissoes"
          @handle:click="fetchEmissoes">
          <Refresh :size="19" />
        </IconButton>
      </div>

      <div v-if="carregandoEmissoes" class="d-flex justify-content-center">
        <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="50" />
      </div>

      <p v-else-if="!emissoes.length" class="vazio">Nenhuma emissão encontrada.</p>

      <Table v-else :headers="['Status', 'CPF', 'RENACH', 'Categoria', 'Emissão', 'Validade', 'Ações']">
        <tr v-for="emissao in emissoes" :key="emissao.id">
          <td>
            <Badge :text="status[emissao.status].texto" :color="status[emissao.status].cor" />
          </td>
          <td>{{ formatarCpf(emissao.cpf_aluno) }}</td>
          <td>{{ emissao.renach }}</td>
          <td>{{ emissao.categoria || '-' }}</td>
          <td>{{ formatarData(emissao.data_emissao || emissao.created_at) }}</td>
          <td>{{ formatarData(emissao.data_validade) }}</td>
          <td>
            <IconButton v-if="emissao.status === 'emitida'" color="primary" title="Informar código"
              :disabled="loading" @handle:click="retomar(emissao)">
              <EmailCheckOutline :size="19" />
            </IconButton>
            <IconButton v-else-if="emissao.status === 'imagem_liberada'" color="secondary"
              title="Ver novamente (envia novo código)" :disabled="loading" @handle:click="retomar(emissao, true)">
              <CardAccountDetailsOutline :size="19" />
            </IconButton>
            <span v-else :title="emissao.mensagem_retorno">-</span>
          </td>
        </tr>
      </Table>
    </Box>
  </PageContainer>
</template>

<script>
import * as spinners from 'epic-spinners'
import PageContainer from 'core/PageContainer'
import Box from 'core/Box'
import Button from 'core/Button'
import IconButton from 'core/IconButton'
import Input from 'core/Input'
import Badge from 'core/Badge'
import Table from 'core/Table'
import urlApi from '../../../router/urlApi'
import urlApiV2 from '../../../router/urlApiV2'
import utils from '../../../services/utils'
import { useAuth } from '@/composables/useAuth'
import Check from 'icons/Check'
import Refresh from 'icons/Refresh'
import Printer from 'icons/Printer'
import Download from 'icons/Download'
import EmailCheckOutline from 'icons/EmailCheckOutline'
import InformationOutline from 'icons/InformationOutline'
import CheckCircleOutline from 'icons/CheckCircleOutline'
import AlertCircleOutline from 'icons/AlertCircleOutline'
import CardAccountDetailsOutline from 'icons/CardAccountDetailsOutline'

const COOLDOWN_REENVIO = 60

export default {
  name: 'EmissaoLadv',
  components: {
    ...spinners,
    PageContainer,
    Box,
    Button,
    IconButton,
    Input,
    Badge,
    Table,
    Check,
    Refresh,
    Printer,
    Download,
    EmailCheckOutline,
    InformationOutline,
    CheckCircleOutline,
    AlertCircleOutline,
    CardAccountDetailsOutline,
  },
  data() {
    return {
      etapas: ['Dados do candidato', 'Código de confirmação', 'LADV emitida'],
      etapa: 1,
      aluno: null,
      cpf: '',
      renach: '',
      codigo: '',
      ladv: null,
      imagem: null,
      cadastroAluno: null,
      emissoes: [],
      cooldown: 0,
      timer: null,
      loading: false,
      vinculando: false,
      carregandoAluno: false,
      carregandoEmissoes: false,
      status: {
        emitida: { texto: 'Aguardando código', cor: 'warning' },
        imagem_liberada: { texto: 'Liberada', cor: 'success' },
        erro: { texto: 'Recusada', cor: 'danger' },
      },
    }
  },
  computed: {
    idAluno() {
      return this.$route.params.idAluno || null
    },
    instrutor() {
      const cpf = (localStorage.getItem('schema') || '').replace('autonomo_', '')
      const nome = localStorage.getItem('nome_fantasia') || ''
      return cpf.length === 11 ? `${nome} - ${utils.formatarCPF(cpf)}` : nome
    },
    imagemSrc() {
      return this.imagem ? `data:${this.imagem.imagemMimeType};base64,${this.imagem.imagemBase64}` : ''
    },
  },
  async created() {
    if (!useAuth().canEmitirLadv) {
      this.toast('Emissão de LADV disponível apenas para instrutor autônomo.')
      this.$router.replace({ name: 'listagem-alunos' })
      return
    }

    await this.carregar()
  },
  watch: {
    // A rota reaproveita o componente (ex.: /alunos/ladv/123 -> "Novo aluno com LADV" no menu)
    idAluno() {
      this.aluno = null
      this.reiniciar()
      this.carregar()
    },
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    async carregar() {
      if (this.idAluno) {
        await this.fetchAluno()
      }

      await this.fetchEmissoes()
    },
    async fetchAluno() {
      this.carregandoAluno = true

      await this.$http.get(`${urlApi}/alunos/${this.idAluno}`)
        .then(response => {
          this.aluno = response.body.dados
          this.cpf = this.aluno.cpf
          this.renach = this.aluno.renach || ''
        })
        .catch(() => {
          this.toast('Aluno não encontrado.')
          this.$router.replace({ name: 'emissao-ladv' })
        })
        .finally(() => {
          this.carregandoAluno = false
        })
    },
    async fetchEmissoes() {
      this.carregandoEmissoes = true

      const url = this.aluno
        ? `${urlApiV2}/getran/ladv/aluno/${this.aluno.id_aluno}`
        : `${urlApiV2}/getran/ladv/pendentes`

      await this.$http.get(url)
        .then(response => {
          this.emissoes = response.body.dados
        })
        .catch(err => {
          this.toastErro(err, 'Erro ao listar as emissões de LADV.')
        })
        .finally(() => {
          this.carregandoEmissoes = false
        })
    },
    async emitir() {
      if (!this.cpf || this.cpf.replace(/\D/g, '').length !== 11) {
        this.toast('Informe um CPF válido.')
        return
      }

      if (!this.renach) {
        this.toast('Informe o RENACH do candidato.')
        return
      }

      this.loading = true

      const data = { cpf: this.cpf, renach: this.renach }
      if (this.aluno) data.aluno_id = this.aluno.id_aluno

      await this.$http.post(`${urlApiV2}/getran/ladv/emitir`, data)
        .then(response => {
          this.ladv = response.body.dados.ladv
          this.cadastroAluno = response.body.dados.aluno
          this.irParaCodigo(COOLDOWN_REENVIO)
          this.toast(response.body.mensagem, 'success')
          this.fetchEmissoes()
        })
        .catch(err => {
          this.toastErro(err, 'Erro ao emitir a LADV.')
          this.fetchEmissoes()
        })
        .finally(() => {
          this.loading = false
        })
    },
    async liberarImagem() {
      if (!/^\d{6}$/.test(this.codigo)) {
        this.toast('Informe o código de 6 dígitos enviado ao candidato.')
        return
      }

      this.loading = true

      await this.$http.post(`${urlApiV2}/getran/ladv/imagem`, {
        token: this.ladv.token_verificacao,
        codigo: this.codigo,
      })
        .then(response => {
          this.imagem = response.body.dados
          this.etapa = 3
          this.fetchEmissoes()
        })
        .catch(err => {
          this.toastErro(err, 'Erro ao liberar a LADV.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    async reenviarCodigo() {
      this.loading = true

      await this.$http.post(`${urlApiV2}/getran/ladv/reenviar-codigo`, { token: this.ladv.token_verificacao })
        .then(response => {
          const dados = response.body.dados || {}
          if (dados.emailMascarado) {
            this.ladv.email_mascarado = dados.emailMascarado
          }
          this.iniciarCooldown(COOLDOWN_REENVIO)
          this.toast(response.body.mensagem, 'success')
        })
        .catch(err => {
          this.toastErro(err, 'Erro ao reenviar o código.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    async vincularAluno() {
      this.vinculando = true

      await this.$http.post(`${urlApiV2}/getran/ladv/${this.ladv.id}/vincular-aluno`)
        .then(response => {
          this.cadastroAluno = response.body.dados
        })
        .catch(err => {
          this.toastErro(err, 'Erro ao cadastrar o aluno.')
        })
        .finally(() => {
          this.vinculando = false
        })
    },
    // Continua uma emissão da lista; LADV já liberada precisa de um código novo (o anterior é de uso único)
    async retomar(emissao, novoCodigo = false) {
      this.ladv = Object.assign({}, emissao)
      this.cadastroAluno = null
      this.irParaCodigo(0)

      if (novoCodigo) {
        await this.reenviarCodigo()
      }

      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    irParaCodigo(cooldown) {
      this.codigo = ''
      this.imagem = null
      this.etapa = 2
      this.iniciarCooldown(cooldown)
    },
    iniciarCooldown(segundos) {
      clearInterval(this.timer)
      this.cooldown = segundos

      if (segundos > 0) {
        this.timer = setInterval(() => {
          this.cooldown -= 1
          if (this.cooldown <= 0) clearInterval(this.timer)
        }, 1000)
      }
    },
    reiniciar() {
      this.etapa = 1
      this.ladv = null
      this.imagem = null
      this.codigo = ''
      this.cadastroAluno = null
      this.iniciarCooldown(0)

      if (!this.aluno) {
        this.cpf = ''
        this.renach = ''
      }
    },
    nomeArquivo() {
      return `LADV_${(this.ladv.cpf_aluno || this.cpf).replace(/\D/g, '')}.jpg`
    },
    baixar() {
      const link = document.createElement('a')
      link.href = this.imagemSrc
      link.download = this.nomeArquivo()
      link.click()
    },
    imprimir() {
      const janela = window.open('', '_blank')

      if (!janela) {
        this.toast('Permita pop-ups para imprimir a LADV.')
        return
      }

      janela.document.write(
        `<html><head><title>${this.nomeArquivo()}</title></head>` +
        '<body style="margin:0;display:flex;justify-content:center">' +
        `<img src="${this.imagemSrc}" style="max-width:100%" onload="window.print();window.close()">` +
        '</body></html>'
      )
      janela.document.close()
    },
    voltar() {
      this.$router.push({ name: 'listagem-alunos' })
    },
    formatarCpf(cpf) {
      return cpf && cpf.length === 11 ? utils.formatarCPF(cpf) : cpf || '-'
    },
    // Laravel 6 serializa datas como "Y-m-d H:i:s"
    formatarData(data) {
      return data ? utils.formatOnlyDate(String(data).slice(0, 10)) : '-'
    },
    toastErro(err, padrao) {
      if (err.body && err.body.errors) {
        Object.values(err.body.errors).flat().forEach(mensagem => this.toast(mensagem))
      } else {
        this.toast((err.body && err.body.mensagem) || padrao)
      }
    },
    toast(text, type = 'error') {
      this.$toasted.show(text, {
        icon: type === 'success' ? 'fa-check-circle' : 'fa-times',
        position: 'top-right',
        duration: 6000,
        fullWidth: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.ladv-container,
.emissoes-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.emissoes-container {
  margin-top: 1.5rem;
  gap: 1rem;

  .emissoes-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 700;
      color: #1c2d5a;
    }
  }

  .vazio {
    margin: 0;
    font-size: 0.875rem;
    color: #8f8f8f;
  }
}

.etapas {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  gap: 1rem;

  li {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-bottom: 0.75rem;
    border-bottom: 3px solid #ececec;
    color: #8f8f8f;

    .numero {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      border: 2px solid currentColor;
      font-weight: 700;
    }

    .titulo {
      font-size: 0.875rem;
      font-weight: 600;
    }

    &.ativa {
      color: #1c2d5a;
      border-color: #1c2d5a;
    }

    &.concluida {
      color: #28a745;
      border-color: #28a745;
    }
  }
}

.etapa {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .descricao {
    margin: 0;
    font-size: 0.875rem;
    color: #606264;
  }

  .inputs {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 1.5rem;

    .input-full {
      flex: 1 1 14rem;
      max-width: 20rem;
    }

    .input-codigo {
      width: 12rem;
      max-width: 100%;
    }
  }

  .buttons {
    align-self: flex-end;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

.aviso,
.email-enviado,
.cadastro-aluno {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: rgba(28, 45, 90, 0.05);
  color: #1c2d5a;
  font-size: 0.875rem;
}

.email-enviado {
  align-items: flex-start;

  h4 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #606264;
  }
}

.cadastro-aluno {
  .texto {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    span {
      color: #606264;
    }
  }

  &.ok {
    background-color: rgba(40, 167, 69, 0.08);
    color: #28a745;
  }

  &.falha {
    background-color: rgba(220, 53, 69, 0.08);
    color: #dc3545;
  }
}

.documento {
  display: flex;
  justify-content: center;
  padding: 1rem;
  border: 1px solid #ececec;
  border-radius: 0.5rem;
  background-color: #fafafa;

  img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
  }
}

@media (max-width: 576px) {
  .etapas li {
    flex: 0 0 auto;

    .titulo {
      display: none;
    }

    &.ativa {
      flex: 1;

      .titulo {
        display: inline;
      }
    }
  }

  .etapa {
    .inputs .input-full,
    .inputs .input-codigo {
      flex-basis: 100%;
      max-width: 100%;
      width: 100%;
    }

    .buttons {
      align-self: stretch;
      flex-direction: column-reverse;
    }
  }

  .cadastro-aluno {
    flex-wrap: wrap;
  }
}
</style>
